import { Controller } from "@hotwired/stimulus";

let popoverSeq = 0;

export default class LsmlPopoverSelectController extends Controller {
  static targets = [
    "select",
    "trigger",
    "triggerIcon",
    "triggerLabel",
    "listbox",
    "option",
    "optionTemplate",
    "customFormTemplate"
  ];

  #observer = null;
  #customSpeakerLabelText = null;

  connect() {
    if (!this.hasSelectTarget || !this.hasTriggerTarget || !this.hasListboxTarget) return;

    this.selectTarget.style.display = "none";
    this.selectTarget.setAttribute("aria-hidden", "true");

    const id = `lsml-popover-${++popoverSeq}`;
    this.listboxTarget.id = id;
    this.triggerTarget.setAttribute("aria-controls", id);

    if (this.element.dataset.controlName) {
      this.triggerTarget.dataset.popoverFor = this.element.dataset.controlName;
    }
    if (this.selectTarget.title) {
      this.triggerTarget.setAttribute("aria-label", this.selectTarget.title);
    }

    this.#assignOptionIds();
    this.#syncTrigger();
    this.#syncDisabled();

    this.#observer = new MutationObserver(this.#onSelectMutation);
    this.#observer.observe(this.selectTarget, {
      attributes: true,
      attributeFilter: [ "selected", "disabled" ],
      childList: true,
      subtree: true
    });
  }

  disconnect() {
    if (this.hasSelectTarget) {
      this.selectTarget.style.display = "";
      this.selectTarget.removeAttribute("aria-hidden");
    }
    if (this.#observer) this.#observer.disconnect();
    this.#observer = null;
  }

  // ------ Action handlers (invoked by Stimulus via data-action) ------

  onTriggerClick(event) {
    event.preventDefault();
    this.#open();
  }

  onTriggerKeydown(event) {
    if (!this.#isOpen()) {
      switch (event.key) {
        case "Enter":
        case " ":
        case "ArrowDown":
        case "ArrowUp":
          event.preventDefault();
          this.#open();
          return;
      }
      if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        this.#open();
        this.#typeAhead(event.key);
      }
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.#moveActive(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.#moveActive(-1);
        break;
      case "Home":
        event.preventDefault();
        this.#setActiveByIndex(0);
        break;
      case "End":
        event.preventDefault();
        this.#setActiveByIndex(this.#optionItems().length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        this.#commitActive();
        break;
      case "Tab":
        this.#commitActive();
        break;
      case "Escape":
        event.preventDefault();
        this.#close();
        this.triggerTarget.focus();
        break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          this.#typeAhead(event.key);
        }
    }
  }

  onListboxClick(event) {
    const item = event.target.closest("[role='option']");
    if (!item) return;
    event.preventDefault();
    if (item.dataset.actionKind === "open-custom-form") {
      this.#renderCustomForm();
      return;
    }
    const index = this.#optionItems().indexOf(item);
    if (index < 0) return;
    this.#setActiveByIndex(index);
    this.#commitActive();
  }

  onSelectChange() {
    this.#renderOptions();
    this.#syncTrigger();
  }

  onDocumentClick(event) {
    if (!this.#isOpen()) return;
    if (this.triggerTarget.contains(event.target)) return;
    if (this.listboxTarget.contains(event.target)) return;
    this.#close();
  }

  onCustomFormCancel(event) {
    event.preventDefault();
    this.#renderOptions();
    this.triggerTarget.focus();
  }

  onCustomFormSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const checkedIcon = form.querySelector("input[name='speaker_icon']:checked");
    const speakerInput = form.querySelector("input[name='speaker']");
    const speaker = (speakerInput?.value || "").trim();
    if (!checkedIcon || !speaker) {
      if (speakerInput && !speaker) speakerInput.focus();
      return;
    }
    const icon = checkedIcon.value;
    const option = document.createElement("option");
    option.value = speaker;
    option.textContent = speaker;
    option.dataset.icon = icon;
    option.dataset.speaker = speaker;
    option.selected = true;
    this.selectTarget.appendChild(option);
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }));

    this.#renderOptions();
    this.#syncTrigger();
    this.#close();
    this.triggerTarget.focus();
  }

  // ------ Option items ------

  #assignOptionIds() {
    const baseId = this.listboxTarget.id;
    this.optionTargets.forEach((item, i) => {
      item.id = `${baseId}-option-${i}`;
    });
  }

  #renderOptions() {
    if (!this.hasOptionTemplateTarget) return;
    const template = this.optionTemplateTarget;

    this.listboxTarget.replaceChildren();

    Array.from(this.selectTarget.options).forEach((option) => {
      const item = template.content.firstElementChild.cloneNode(true);
      item.setAttribute("aria-selected", option.selected ? "true" : "false");
      item.dataset.value = option.value;
      const icon = item.querySelector(".__lsml-editor_popover-icon");
      if (icon) icon.textContent = option.dataset.icon || "";
      const label = item.querySelector(".__lsml-editor_popover-label");
      if (label) label.textContent = option.textContent;
      this.listboxTarget.appendChild(item);
    });

    if (this.hasCustomFormTemplateTarget) {
      const customItem = template.content.firstElementChild.cloneNode(true);
      customItem.classList.add("__lsml-editor_popover-option-custom");
      customItem.setAttribute("aria-selected", "false");
      delete customItem.dataset.value;
      customItem.dataset.actionKind = "open-custom-form";
      const icon = customItem.querySelector(".__lsml-editor_popover-icon");
      if (icon) icon.textContent = "";
      const label = customItem.querySelector(".__lsml-editor_popover-label");
      if (label) label.textContent = this.#customSpeakerLabel();
      this.listboxTarget.appendChild(customItem);
    }

    this.#assignOptionIds();
  }

  #customSpeakerLabel() {
    if (this.#customSpeakerLabelText) return this.#customSpeakerLabelText;
    const initial = this.listboxTarget.querySelector("[data-action-kind='open-custom-form'] .__lsml-editor_popover-label");
    this.#customSpeakerLabelText = initial?.textContent ?? "Custom speaker…";
    return this.#customSpeakerLabelText;
  }

  // ------ Custom form ------

  #renderCustomForm() {
    if (!this.hasCustomFormTemplateTarget) return;
    this.listboxTarget.replaceChildren();
    const fragment = this.customFormTemplateTarget.content.cloneNode(true);
    this.listboxTarget.appendChild(fragment);
    this.triggerTarget.removeAttribute("aria-activedescendant");

    requestAnimationFrame(() => {
      const speakerInput = this.listboxTarget.querySelector("input[name='speaker']");
      if (speakerInput) speakerInput.focus();
    });
  }

  // ------ Open/close ------

  #open() {
    this.listboxTarget.hidden = false;
    this.triggerTarget.setAttribute("aria-expanded", "true");
    if (!this.triggerTarget.getAttribute("aria-activedescendant")) {
      const initial = this.selectTarget.selectedIndex >= 0 ? this.selectTarget.selectedIndex : 0;
      this.#setActiveByIndex(initial);
    }
  }

  #close() {
    this.listboxTarget.hidden = true;
    this.triggerTarget.setAttribute("aria-expanded", "false");
    this.triggerTarget.removeAttribute("aria-activedescendant");
  }

  #isOpen() {
    return this.triggerTarget.getAttribute("aria-expanded") === "true";
  }

  // ------ Active descendant ------

  #optionItems() {
    return Array.from(this.listboxTarget.children);
  }

  #activeIndex() {
    const id = this.triggerTarget.getAttribute("aria-activedescendant");
    if (!id) return -1;
    return this.#optionItems().findIndex((item) => item.id === id);
  }

  #setActiveByIndex(index) {
    const items = this.#optionItems();
    if (items.length === 0) return;
    const wrapped = ((index % items.length) + items.length) % items.length;
    items.forEach((item, i) => {
      item.dataset.active = i === wrapped ? "true" : "false";
    });
    this.triggerTarget.setAttribute("aria-activedescendant", items[wrapped].id);
  }

  #moveActive(delta) {
    const current = this.#activeIndex();
    const start = current === -1 ? 0 : current + delta;
    this.#setActiveByIndex(start);
  }

  #typeAhead(char) {
    const items = this.#optionItems();
    if (items.length === 0) return;
    const lower = char.toLowerCase();
    const startIndex = (this.#activeIndex() + 1) % items.length;
    for (let i = 0; i < items.length; i++) {
      const idx = (startIndex + i) % items.length;
      if (items[idx].textContent.trim().toLowerCase().startsWith(lower)) {
        this.#setActiveByIndex(idx);
        return;
      }
    }
  }

  // ------ Mutations ------

  #onSelectMutation = (mutations) => {
    let needsRerender = false;
    let needsDisabledSync = false;
    for (const m of mutations) {
      if (m.type === "childList") needsRerender = true;
      if (m.type === "attributes" && m.attributeName === "selected") needsRerender = true;
      if (m.type === "attributes" && m.attributeName === "disabled") needsDisabledSync = true;
    }
    if (needsRerender) {
      this.#renderOptions();
      this.#syncTrigger();
    }
    if (needsDisabledSync) this.#syncDisabled();
  };

  #syncDisabled() {
    this.triggerTarget.disabled = this.selectTarget.disabled;
    if (this.selectTarget.disabled && this.#isOpen()) this.#close();
  }

  #commitActive() {
    const index = this.#activeIndex();
    if (index < 0) return;
    const item = this.#optionItems()[index];
    if (item && item.dataset.actionKind === "open-custom-form") {
      this.#renderCustomForm();
      return;
    }
    this.#commitIndex(index);
  }

  #commitIndex(index) {
    const option = this.selectTarget.options[index];
    if (!option) return;
    if (!option.selected) {
      this.selectTarget.selectedIndex = index;
      option.selected = true;
      this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }));
    }
    this.#syncTrigger();
    this.#close();
    this.triggerTarget.focus();
  }

  // ------ Sync ------

  #syncTrigger() {
    const selected = this.selectTarget.selectedOptions[0];
    if (this.hasTriggerIconTarget) {
      this.triggerIconTarget.textContent = selected?.dataset.icon || "";
    }
    if (this.hasTriggerLabelTarget) {
      this.triggerLabelTarget.textContent = selected ? selected.textContent : "";
    }
  }
}
